import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

import { SelloutHeader } from './sellout.model';
import { Page } from '../models/page-wrapper.model';
import { SelloutAttachment } from './sellout-attachment.model';

export interface SelloutUploadResponse {
  selloutId: number;
  batchNumber: string;
  validationStatus: string;
  message: string;
}

export interface SelloutSearchParams {
  customerNumber?: string;
  customerName?: string;
  city?: string;
  channel?: string;
  batchNumber?: string;
  status?: string;
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  timelineStage?: string;
  settlementId?: number;
  page?: number;
  size?: number;
  sort?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SelloutService {

  private apiUrl = `${environment.restApiUrl}sellouts`;

  constructor(private http: HttpClient) { }

  // Method to upload the promo sellout Excel file
  uploadSellout(sessionId: number, fileUrl: string, settlementId: number): Observable<SelloutUploadResponse> {
    const url = `${this.apiUrl}/sessions/${sessionId}/sellout`;
    return this.http.post<SelloutUploadResponse>(url, { fileUrl, settlementId });
  }

  private buildSearchParams(searchParams: SelloutSearchParams): HttpParams {
    let params = new HttpParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (value instanceof Date) {
          params = params.append(key, value.toISOString().split('T')[0]); // Format as YYYY-MM-DD
        } else if (Array.isArray(value)) {
          value.forEach(item => params = params.append(key, item));
        } else {
          params = params.append(key, String(value));
        }
      }
    });
    return params;
  }

  // --- SelloutHeader Endpoints ---

  getAllSelloutHeaders(): Observable<SelloutHeader[]> {
    return this.http.get<SelloutHeader[]>(`${this.apiUrl}/headers`);
  }

  searchSelloutHeaders(searchParams: SelloutSearchParams): Observable<Page<SelloutHeader>> {
    const params = this.buildSearchParams(searchParams);
    return this.http.get<Page<SelloutHeader>>(`${this.apiUrl}/headers/search`, { params });
  }

  getSelloutHeaderById(id: number): Observable<SelloutHeader> {
    return this.http.get<SelloutHeader>(`${this.apiUrl}/headers/${id}`);
  }

  createSelloutHeader(header: SelloutHeader): Observable<SelloutHeader> {
    return this.http.post<SelloutHeader>(`${this.apiUrl}/headers`, header);
  }

  updateSelloutHeader(id: number, header: SelloutHeader): Observable<SelloutHeader> {
    return this.http.put<SelloutHeader>(`${this.apiUrl}/headers/${id}`, header);
  }

  applyHeaderOverride(id: number, overrideJustification: string): Observable<SelloutHeader> {
    return this.http.post<SelloutHeader>(`${this.apiUrl}/headers/${id}/override`, { overrideJustification });
  }

  deleteSelloutHeader(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/headers/${id}`);
  }

  uploadAttachments(selloutId: number, attachments: SelloutAttachment[]): Observable<SelloutAttachment[]> {
    const url = `${this.apiUrl}/headers/${selloutId}/attachments`;
    return this.http.post<SelloutAttachment[]>(url, attachments);
  }

  getAttachmentViewUrl(selloutId: number, attachmentId: number): Observable<{ url: string }> {
    const url = `${this.apiUrl}/headers/${selloutId}/attachments/${attachmentId}/view-url`;
    return this.http.get<{ url: string }>(url);
  }

}
